import { useCallback, useEffect, useState } from "react";

// Shadcn UI Components

// Lucide Icons
import { CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";

import MeetingDialog from "./component/MeetingDialog";
import MeetingFilters from "./component/MeetingFilters";
import MeetingStats from "./component/MeetingStats";
import MeetingTable from "./component/MeetingTable";

// Meetings Component
const Meetings = () => {
  const { users } = useData();
  const api = useApi();
  const [showAddMeeting, setShowAddMeeting] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("meetingDate");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);

  const pagination = usePagination(10);
  const { items: meetings, currentPage: page, pageSize, setPaginationData, setCurrentPage: setPage } = pagination;

  const { user } = useAuth();

  useEffect(() => {
    if (page === 0 && meetings.length > 0) {
      checkUpcomingMeetings(meetings);
    }
  }, [meetings, page]);

  // The hook handles page changes via useEffect in fetchMeetings

  const checkUpcomingMeetings = (meetingsList) => {
    const now = new Date();
    const upcoming = meetingsList.filter((meeting) => {
      if (!meeting.meetingDate || !meeting.meetingTime) return false;

      const [hours, minutes] = meeting.meetingTime.split(":");
      const meetingDateTime = new Date(meeting.meetingDate);
      meetingDateTime.setHours(Number(hours), Number(minutes), 0, 0);
      const diffMs = meetingDateTime - now;
      const diffMins = diffMs / 60000;
      return diffMins > -5 && diffMins <= 30;
    });

    if (upcoming.length > 0) {
      upcoming.forEach((m) => {
        toast.info(`Upcoming: ${m.title} at ${m.meetingTime}`);
      });
    }
  };

  const fetchMeetings = useCallback(
    async (pageToLoad) => {
      try {
        setLoading(true);
        const response = await api.get(`/meetings`, {
          params: {
            page: pageToLoad,
            size: pageSize,
            search: searchTerm.trim() || undefined,
            sortBy: sortBy || undefined,
            direction: direction || undefined,
            showDeleted
          }
        });
        setPaginationData(response.data);
      } catch (error) {
        toast.error(error.message || "Failed to fetch meetings");
      } finally {
        setLoading(false);
      }
    },
    [api, searchTerm, pageSize, sortBy, direction, showDeleted, setPaginationData]
  );

  useEffect(() => {
    fetchMeetings(page);
  }, [page, fetchMeetings]);

  const handleCreateMeeting = async (meetingData) => {
    try {
      await api.post("/meetings", meetingData);
      toast.success("Meeting scheduled successfully");
      fetchMeetings();
      setShowAddMeeting(false);
    } catch (error) {
      toast.error(error.message || "Failed to schedule meeting");
    }
  };

  const handleUpdateMeeting = async (meetingData) => {
    try {
      await api.put(`/meetings/${meetingData.id}`, meetingData);
      toast.success("Meeting updated successfully");
      fetchMeetings();
      setEditingMeeting(null);
    } catch (error) {
      toast.error(error.message || "Failed to update meeting");
    }
  };

  const confirm = useConfirm();

  const deleteMeeting = async (meetingId) => {
    const isConfirmed = await confirm({
      title: "Delete Meeting",
      message: "Are you sure you want to delete this meeting?"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/meetings/${meetingId}`);
      toast.success("Meeting deleted successfully");
      fetchMeetings();
    } catch (error) {
      toast.error(error.message || "Failed to delete meeting");
    }
  };

  const updateMeetingStatus = async (meetingId, status) => {
    const meetingToUpdate = meetings.find((m) => m.id === meetingId);
    if (!meetingToUpdate) return;

    try {
      await api.put(`/meetings/${meetingId}`, {
        ...meetingToUpdate,
        status: status
      });
      toast.success("Meeting status updated");
      fetchMeetings();
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "rescheduled":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "bg-red-500";
      case "high":
        return "bg-orange-500";
      case "medium":
        return "bg-yellow-500";
      case "low":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  const filteredMeetings = meetings.filter((meeting) => {
    switch (filter) {
      case "today":
        return new Date(meeting.meetingDate).toDateString() === new Date().toDateString();
      case "upcoming":
        return new Date(meeting.meetingDate) > new Date() && meeting.status.toLowerCase() === "scheduled";
      case "completed":
        return meeting.status.toLowerCase() === "completed";
      case "overdue":
        return new Date(meeting.meetingDate) < new Date() && meeting.status.toLowerCase() === "scheduled";
      default:
        return true;
    }
  });

  if (loading && meetings.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yale-blue-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading meetings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 space-y-6">
        <PageHeader
          title="Meetings"
          subtitle="Manage and track client meetings and discussions"
          actionLabel={user?.permissions?.meeting?.create ? "Schedule Meeting" : null}
          onActionClick={() => setShowAddMeeting(true)}
          icon={Users}
          pageIcon={CalendarDays}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput
                placeholder="Search meetings..."
                className="md:w-72"
                onSearch={(val) => {
                  setSearchTerm(val);
                  setPage(0);
                }}
              />
            </div>
          }
        />

        <MeetingStats meetings={meetings} onFilterChange={setFilter} />

        <MeetingFilters currentFilter={filter} onFilterChange={setFilter} />

        <MeetingTable
          meetings={filteredMeetings}
          permissions={{
            canEdit: user?.permissions?.meeting?.update,
            canDelete: user?.permissions?.meeting?.delete
          }}
          onEdit={setEditingMeeting}
          onDelete={deleteMeeting}
          onUpdateStatus={updateMeetingStatus}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          sortBy={sortBy}
          direction={direction}
          onSort={(key, dir) => {
            setSortBy(key);
            setDirection(dir);
          }}
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          setPage={setPage}
        />

        <MeetingDialog
          isOpen={showAddMeeting}
          onOpenChange={setShowAddMeeting}
          onSubmit={handleCreateMeeting}
          title="Schedule New Meeting"
          description="Create a new meeting with all the necessary details"
        />

        <MeetingDialog
          isOpen={!!editingMeeting}
          onOpenChange={(open) => !open && setEditingMeeting(null)}
          meeting={editingMeeting}
          onSubmit={handleUpdateMeeting}
          title="Edit Meeting"
          description="Update meeting details, attendees, or status"
        />
      </div>

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>
    </div>
  );
};

export default Meetings;
